import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Save, Send } from "react-feather";
import {
  useGetSettingsQuery,
  useSetSettingMutation,
  useTestEmailSettingMutation,
  useTestWhatsAppSettingMutation,
} from "../settingsApi";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";

const GROUP_TITLES = {
  Company: "Company (Quotation Letterhead)",
  Email: "Email (SMTP)",
  WhatsApp: "WhatsApp (Twilio)",
  PettyCash: "Petty Cash",
  Vehicles: "Vehicles",
};
const GROUP_ORDER = ["Company", "Email", "WhatsApp", "PettyCash", "Vehicles"];

// "Smtp.FromAddress" -> "From Address"
const keyLabel = (settingKey) =>
  settingKey
    .split(".")
    .pop()
    .replace(/([a-z])([A-Z])/g, "$1 $2");

// One setting = one row, its own local edit state seeded once from the
// server value (secrets always start blank - the server never returns a
// real secret value, only "***configured***"/null, and re-sending that
// mask string back would overwrite the real secret). Local state instead
// of a shared parent object keyed by settingKey deliberately, so saving one
// row (which refetches the whole list, handoff §18) never clobbers
// whatever another row's input currently has typed into it.
//
// `hasValue` folds together the global case (a real value/mask is present)
// and the per-branch case (an override row exists for this branch at all,
// handoff §18.1) - a branch with no override shows "Not set for this
// branch" and starts blank, same as an unset secret, rather than silently
// falling back to the global value.
const SettingRow = ({ setting, hasValue, scopeLabel, onSave, saving }) => {
  const isSecret = setting.isSecret;
  const [value, setValue] = useState(isSecret || !hasValue ? "" : setting.value ?? "");
  const isUseSsl = setting.settingKey === "Smtp.UseSsl";
  const configured = isSecret ? setting.value === "***configured***" : hasValue;

  const handleSave = async () => {
    if (!value.trim()) {
      toast.error("Enter a value first - settings can't be cleared back to blank, only overwritten");
      return;
    }
    const ok = await onSave(setting.settingKey, value.trim());
    if (ok && isSecret) setValue("");
  };

  return (
    <div className="row align-items-center mb-3">
      <div className="col-md-3">
        <label className="form-label mb-0">{keyLabel(setting.settingKey)}</label>
        <div>
          <span className={`badge ${configured ? "badge-linesuccess" : "text-bg-secondary"}`}>
            {configured ? "Configured" : `Not set${scopeLabel ? ` for ${scopeLabel}` : ""}`}
          </span>
        </div>
        {setting.description && <div className="text-muted small">{setting.description}</div>}
      </div>
      <div className="col-md-6">
        {isUseSsl ? (
          <select className="form-control" value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">--Select--</option>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : (
          <input
            type={isSecret ? "password" : "text"}
            className="form-control"
            value={value}
            placeholder={isSecret ? (configured ? "Leave blank to keep current value" : "Not set") : ""}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </div>
      <div className="col-md-3">
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleSave} disabled={saving}>
          <Save size={14} className="me-1" /> Save
        </button>
      </div>
    </div>
  );
};

// Generic admin-configurable settings (handoff §18) - SMTP/WhatsApp
// credentials for Sales quotation delivery, company letterhead details, the
// Petty Cash self-pay ceiling, and (added 2026-08-07) each branch's Vehicle
// Maintenance spares product group, all sharing one key/value store.
// Settings.ManageIntegrations-gated end to end (route + every call here) -
// SYSTEM_ADMIN only, no partial read-only view exists since even GET
// requires the same permission.
//
// Per-branch overrides (handoff §18.1) reuse the same header BranchPicker
// every other master screen uses, rather than a second scope selector on
// this page - "All branches" in the header means Global settings here (the
// same `selectedBranchId === null` the API's own branchId=0 default already
// means), picking a specific branch switches every row to that branch's own
// override, editing independently of the global value. Verified live: a
// branch with no override for a key returns no row for it at all, so rows
// are always driven off the GLOBAL list (the stable "every known key"
// catalog) with the branch-scoped list only overlaid on top when selected.
const SettingsPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { data: globalSettings = [], isLoading: isLoadingGlobal } = useGetSettingsQuery(undefined);
  const { data: branchSettings = [], isLoading: isLoadingBranch } = useGetSettingsQuery(selectedBranchId, {
    skip: !selectedBranchId,
  });
  const [setSetting, { isLoading: isSaving }] = useSetSettingMutation();
  const [testEmail, { isLoading: isTestingEmail }] = useTestEmailSettingMutation();
  const [testWhatsApp, { isLoading: isTestingWhatsApp }] = useTestWhatsAppSettingMutation();

  const [testAddress, setTestAddress] = useState("");
  const [testPhone, setTestPhone] = useState("");

  const isLoading = isLoadingGlobal || (Boolean(selectedBranchId) && isLoadingBranch);
  const scopeBranch = selectedBranchId
    ? availableBranches.find((b) => b.branchId === selectedBranchId)
    : null;
  const scopeLabel = scopeBranch?.branchCode || null;

  // Every row is driven off the global key catalog (stable, always all
  // known keys); when a branch is selected, overlay that branch's own
  // override row (by settingKey) in place of the global one, or mark it
  // unset if no override exists yet for this branch.
  const effectiveRows = useMemo(
    () =>
      globalSettings.map((globalRow) => {
        if (!selectedBranchId) return { setting: globalRow, hasValue: globalRow.value != null };
        const override = branchSettings.find((b) => b.settingKey === globalRow.settingKey);
        return {
          setting: override || { ...globalRow, value: null },
          hasValue: Boolean(override),
        };
      }),
    [globalSettings, branchSettings, selectedBranchId]
  );

  const grouped = useMemo(() => {
    const map = {};
    effectiveRows.forEach((row) => {
      const group = row.setting.settingGroup;
      if (!map[group]) map[group] = [];
      map[group].push(row);
    });
    return map;
  }, [effectiveRows]);

  const handleSave = async (key, value) => {
    try {
      await setSetting({ key, value, branchId: selectedBranchId || undefined }).unwrap();
      toast.success("Setting saved");
      return true;
    } catch (error) {
      toast.error(error?.message || "Could not save setting");
      return false;
    }
  };

  const handleTestEmail = async () => {
    if (!testAddress.trim()) {
      toast.error("Enter an address to send the test to");
      return;
    }
    try {
      await testEmail(testAddress.trim()).unwrap();
      toast.success("Test email sent");
    } catch (error) {
      toast.error(error?.message || "Test email failed");
    }
  };

  const handleTestWhatsApp = async () => {
    if (!testPhone.trim()) {
      toast.error("Enter a phone number to send the test to");
      return;
    }
    try {
      await testWhatsApp(testPhone.trim()).unwrap();
      toast.success("Test WhatsApp message sent");
    } catch (error) {
      toast.error(error?.message || "Test WhatsApp message failed");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>System Settings</h4>
              <h6>Email, WhatsApp, company letterhead, and other integration parameters</h6>
            </div>
          </div>
        </div>

        <div className="alert alert-info py-2">
          {scopeLabel
            ? `Editing overrides for ${scopeLabel} - use the branch picker in the header to switch branches or return to Global.`
            : "Editing Global defaults - select a branch in the header to set a branch-specific override instead."}
        </div>

        {isLoading && <p className="text-muted">Loading...</p>}

        {GROUP_ORDER.filter((g) => grouped[g]).map((group) => (
          <div className="card mb-4" key={group}>
            <div className="card-body">
              <h5 className="mb-3">{GROUP_TITLES[group] || group}</h5>
              {grouped[group].map(({ setting, hasValue }) => (
                <SettingRow
                  key={`${setting.settingKey}-${selectedBranchId ?? "global"}`}
                  setting={setting}
                  hasValue={hasValue}
                  scopeLabel={scopeLabel}
                  onSave={handleSave}
                  saving={isSaving}
                />
              ))}

              {group === "Email" && (
                <div className="row align-items-center mt-2 pt-3 border-top">
                  <div className="col-md-3">
                    <label className="form-label mb-0">Send Test Email</label>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="email"
                      className="form-control"
                      placeholder="someone@example.com"
                      value={testAddress}
                      onChange={(e) => setTestAddress(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleTestEmail}
                      disabled={isTestingEmail}
                    >
                      <Send size={14} className="me-1" /> Send Test
                    </button>
                  </div>
                </div>
              )}

              {group === "WhatsApp" && (
                <div className="row align-items-center mt-2 pt-3 border-top">
                  <div className="col-md-3">
                    <label className="form-label mb-0">Send Test WhatsApp</label>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="+254700000000"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleTestWhatsApp}
                      disabled={isTestingWhatsApp}
                    >
                      <Send size={14} className="me-1" /> Send Test
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SettingsPage;
